import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useMe } from "../lib/me";

type Role = "admin" | "member";

interface UserRow {
  id: number;
  email: string | null;
  name: string | null;
  role: Role;
  allowed_tools: string;
  totp_confirmed_at: string | null;
  last_login_at: string | null;
  disabled: number;
  invite_expires_at: string | null;
}

const TOOL_OPTIONS: { key: string; label: string }[] = [
  { key: "collection", label: "Collection" },
  { key: "payroll",    label: "Payroll" },
  { key: "wht",        label: "WHT Monitor" },
  { key: "monday",     label: "Contacts" },
  { key: "outreach",   label: "Outreach" },
];

function parseAllowed(raw: string): string[] | "all" {
  try {
    const v = JSON.parse(raw);
    if (v === "all") return "all";
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

export default function Admin() {
  const me = useMe();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await api<{ users: UserRow[] }>("/api/admin/users");
    setRows(r.users);
  }
  useEffect(() => { refresh(); }, []);

  async function resendInvite(u: UserRow) {
    if (!confirm(`Resend invite to ${u.email}? This will reset their TOTP if they had it.`)) return;
    setBusy(true);
    try {
      const r = await api<{ inviteSent: boolean; inviteError?: string; inviteUrl: string }>(`/api/admin/users/${u.id}/resend-invite`, { method: "POST" });
      if (!r.inviteSent) alert(`Invite email failed: ${r.inviteError}\n\nManual URL:\n${r.inviteUrl}`);
      else alert("Invite sent.");
      refresh();
    } finally { setBusy(false); }
  }

  async function toggleDisabled(u: UserRow) {
    setBusy(true);
    try {
      await api(`/api/admin/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ disabled: !u.disabled }) });
      refresh();
    } finally { setBusy(false); }
  }

  async function removeUser(u: UserRow) {
    if (!confirm(`Delete ${u.email}? This is permanent.`)) return;
    setBusy(true);
    try {
      await api(`/api/admin/users/${u.id}`, { method: "DELETE" });
      refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Delete failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">{rows.length} user{rows.length !== 1 ? "s" : ""} · admin-only page</p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(true)}>+ New user</button>
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th>Name / email</th>
              <th>Role</th>
              <th>Allowed tools</th>
              <th>Status</th>
              <th>Last login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const allowed = parseAllowed(u.allowed_tools);
              return (
                <tr key={u.id}>
                  <td>
                    <div className="font-medium text-slate-900">{u.name || <span className="text-slate-400">—</span>}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td>
                    <span className={`pill ${u.role === "admin" ? "pill-blue" : "pill-slate"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="text-xs">
                    {allowed === "all"
                      ? <span className="font-medium text-slate-700">All tools</span>
                      : (allowed.length === 0
                          ? <span className="text-slate-400">None</span>
                          : allowed.join(", "))}
                  </td>
                  <td>
                    {u.disabled
                      ? <span className="pill pill-rose">disabled</span>
                      : u.totp_confirmed_at
                        ? <span className="pill pill-green">active</span>
                        : <span className="pill pill-amber">pending</span>}
                  </td>
                  <td className="text-xs text-slate-500">
                    {u.last_login_at ? new Date(u.last_login_at.replace(" ", "T") + "Z").toLocaleString("en-GB") : "—"}
                  </td>
                  <td className="pr-3 text-right space-x-1 whitespace-nowrap">
                    <button className="chip bg-slate-100 ring-1 ring-slate-200 hover:bg-slate-200" onClick={() => setEditing(u)}>Edit</button>
                    {u.email && (
                      <button disabled={busy} className="chip bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 hover:bg-indigo-100" onClick={() => resendInvite(u)}>
                        {u.totp_confirmed_at ? "Reset TOTP" : "Resend invite"}
                      </button>
                    )}
                    {me?.id !== u.id && (
                      <>
                        <button disabled={busy} className="chip bg-amber-50 text-amber-800 ring-1 ring-amber-200/60 hover:bg-amber-100" onClick={() => toggleDisabled(u)}>
                          {u.disabled ? "Enable" : "Disable"}
                        </button>
                        <button disabled={busy} className="chip bg-rose-50 text-rose-700 ring-1 ring-rose-200/60 hover:bg-rose-100" onClick={() => removeUser(u)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && <UserDialog onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} />}
      {editing && <UserDialog user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function UserDialog({
  user, onClose, onSaved,
}: { user?: UserRow; onClose: () => void; onSaved: () => void }) {
  const editing = !!user;
  const initialAllowed = user ? parseAllowed(user.allowed_tools) : "all" as const;
  const initialKeys = initialAllowed === "all" ? [] : initialAllowed;
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [role, setRole] = useState<Role>(user?.role || "member");
  const [allowAll, setAllowAll] = useState(initialAllowed === "all");
  const [tools, setTools] = useState<Set<string>>(new Set(initialKeys));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ inviteSent: boolean; inviteUrl: string; inviteError?: string } | null>(null);

  function toggleTool(k: string) {
    const next = new Set(tools);
    if (next.has(k)) next.delete(k); else next.add(k);
    setTools(next);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const allowed_tools = allowAll ? "all" : Array.from(tools);
    try {
      if (editing) {
        await api(`/api/admin/users/${user!.id}`, { method: "PATCH", body: JSON.stringify({ name, role, allowed_tools }) });
        onSaved();
      } else {
        const r = await api<{ id: number; inviteSent: boolean; inviteUrl: string; inviteError?: string }>("/api/admin/users", {
          method: "POST",
          body: JSON.stringify({ name, email, role, allowed_tools }),
        });
        if (!r.inviteSent) setResult({ inviteSent: false, inviteUrl: r.inviteUrl, inviteError: r.inviteError });
        else { onSaved(); }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally { setBusy(false); }
  }

  if (result && !result.inviteSent) {
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
        <div className="card max-w-md space-y-3 p-6">
          <div className="text-base font-semibold text-amber-700">Invite email could not be sent</div>
          <div className="text-sm text-slate-600">{result.inviteError}</div>
          <div className="text-xs text-slate-500">Send this URL manually to the user (valid 7 days):</div>
          <textarea readOnly value={result.inviteUrl} className="w-full font-mono text-xs" rows={3} onFocus={(e) => e.target.select()} />
          <div className="text-right"><button className="btn-primary" onClick={onSaved}>OK</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <form onSubmit={save} className="card w-full max-w-md space-y-4 p-6">
        <div className="text-base font-semibold tracking-tightish">{editing ? `Edit ${user!.email}` : "Invite new user"}</div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Name</label>
          <input className="w-full" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        {!editing && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Email</label>
            <input type="email" className="w-full" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="staff@mailbit.co.th" />
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Role</label>
          <select className="w-full" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="member">Member</option>
            <option value="admin">Admin (full access + manages users)</option>
          </select>
        </div>
        <div>
          <label className="mb-2 block text-xs font-medium text-slate-600">Tool access</label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allowAll} onChange={(e) => setAllowAll(e.target.checked)} />
            <span className="font-medium">All tools</span>
          </label>
          {!allowAll && (
            <div className="mt-2 space-y-1 pl-6">
              {TOOL_OPTIONS.map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={tools.has(t.key)} onChange={() => toggleTool(t.key)} />
                  {t.label}
                </label>
              ))}
            </div>
          )}
        </div>
        {error && <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{error}</div>}
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "Saving…" : editing ? "Save" : "Invite"}
          </button>
        </div>
      </form>
    </div>
  );
}
